import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { openPopUp } from '@containers/App/containers/PopUp/actions';
import POPUP_TYPES from '@containers/App/containers/PopUp/types';
import { ROUTES, TOAST_AUTO_CLOSE_TIME } from '@shared/constants';
import { USER_PAGE_TYPES } from '@containers/Admin/containers/UserPage/consts';
import MentorFormEditor from './components/MentorFormEditor';
import MentorFormsTable from './components/MentorFormsTable';
import { createMentorForm, deleteMentorForm, getMentorForms, updateMentorForm } from './services';
import { MentorFormActivity, SurveyResponse } from './types';
import { Wrapper } from './styles';
import { mentorRoles, ROLES } from '@shared/constants/roles';

// static route param used for new (not yet created) mentor forms
const NEW_MENTOR_FORM_ID = 'new';

const MentorForms = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id: userId, formActivityId } = useParams<{ id: string; formActivityId?: string }>();
  const { id: currentUserId, roles } = useSelector((state: RootStateOrAny) =>
    state.getIn(['app', 'authentication', 'user']).toJS(),
  );
  const [formsActivity, setFormsActivity] = useState<MentorFormActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingFormActivityId, setEditingFormActivityId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const selectedFormActivity = useMemo(
    () => formsActivity.find((form) => form.id === formActivityId),
    [formsActivity, formActivityId],
  );
  const mode = !formActivityId
    ? 'list'
    : formActivityId === NEW_MENTOR_FORM_ID
      ? 'add'
      : editingFormActivityId === formActivityId
        ? 'edit'
        : 'view';

  const isTechAdmin = useMemo(() => roles?.includes(ROLES.TECH_ADMIN), [roles]);
  const isClinicalMentor = roles.some((roleId: string) => mentorRoles.includes(roleId));

  const canManageForm = (form: MentorFormActivity) => isTechAdmin || form.createdBy?.id === currentUserId;

  const loadMentorForms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!userId) {
        throw new Error('User ID is required to load mentor forms');
      }
      setFormsActivity(await getMentorForms(userId));
    } catch (e) {
      setError('Failed to load mentor forms.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadMentorForms();
  }, [loadMentorForms]);

  const navigateToForm = (formActivityIdParam: string) => {
    history.push(
      generatePath(ROUTES.USERS.MENTOR_FORM, { id: userId, formActivityId: formActivityIdParam }),
      location.state,
    );
  };

  const handleAddForm = async () => {
    if (deletingId) return;
    setEditingFormActivityId(null);
    navigateToForm(NEW_MENTOR_FORM_ID);
  };

  const handleViewForm = (form: MentorFormActivity) => {
    if (!form.formId || !form.surveySchema || deletingId) {
      toast.error('This mentor form cannot be viewed because its template is unavailable.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
      return;
    }

  setEditingFormActivityId(null);
  navigateToForm(form.id);
  };

  const handleEditForm = (form: MentorFormActivity) => {
    if (!canManageForm(form)) return;

    if (!form.formId || !form.surveySchema || deletingId) {
      toast.error('This mentor form cannot be edited because its template is unavailable.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
      return;
    }

  setEditingFormActivityId(form.id);
  navigateToForm(form.id);
  };

  const handleComplete = async (surveyResponse: SurveyResponse, newFormId?: string) => {
    if (!userId || saving) return;

    if (mode === 'edit' && (!selectedFormActivity || !canManageForm(selectedFormActivity))) {
      return;
    }

    if (mode === 'add' && !newFormId) {
      return;
    }

    setSaving(true);
    try {
      if (mode === 'edit' && selectedFormActivity) {
        const updated = await updateMentorForm(selectedFormActivity.id, surveyResponse);
        setFormsActivity((current) =>
          current.map((form) => (form.id === selectedFormActivity.id ? { ...form, ...updated } : form)),
        );
      } else if (mode === 'add' && newFormId) {
        await createMentorForm(userId, newFormId, surveyResponse);
        await loadMentorForms();
      }
      setEditingFormActivityId(null);
      history.push(
        generatePath(ROUTES.USERS.PAGE, { id: userId, pageType: USER_PAGE_TYPES.MENTOR_FORMS }),
        location.state,
      );
    } catch (e) {
      toast.error('Failed to save mentor form. Please try again.', {
        autoClose: TOAST_AUTO_CLOSE_TIME,
        position: 'bottom-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForm = (form: MentorFormActivity) => {
    if (saving || deletingId || !canManageForm(form)) return;

    dispatch(
      openPopUp(POPUP_TYPES.CONFIRM, {
        titleText: 'Delete mentor form',
        bodyText: `Are you sure you want to delete this mentor form?\n${form.name || 'Mentor form'}`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        disableBackdrop: true,
        onConfirm: async (confirm: boolean) => {
          if (!confirm) return;

          setDeletingId(form.id);
          try {
            await deleteMentorForm(form.id);
            setFormsActivity((current) => current.filter(({ id }) => id !== form.id));
          } catch (e) {
            toast.error('Failed to delete mentor form.', {
              autoClose: TOAST_AUTO_CLOSE_TIME,
              position: 'bottom-right',
            });
          } finally {
            setDeletingId(null);
          }
        },
      }),
    );
  };

  return (
    <Wrapper>
      {mode !== 'list' ? (
        <MentorFormEditor
          userId={userId}
          isClinicalMentor={isClinicalMentor}
          formActivityId={selectedFormActivity?.id}
          selectedFormName={selectedFormActivity?.name || ''}
          saving={saving}
          mode={mode}
          schema={selectedFormActivity?.surveySchema}
          initialData={selectedFormActivity?.surveyResponse}
          onComplete={handleComplete}
        />
      ) : (
        <MentorFormsTable
          forms={formsActivity}
          currentUserId={currentUserId}
          isTechAdmin={isTechAdmin}
          loading={loading}
          error={error}
          onAdd={handleAddForm}
          onView={handleViewForm}
          onEdit={handleEditForm}
          onDelete={handleDeleteForm}
          deletingId={deletingId}
        />
      )}
    </Wrapper>
  );
};

export default MentorForms;
